import { flexRender } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import {
  TableHead,
  TableRow,
  TableHeader as ShadcnTableHeader,
} from '@/components/ui/table';
import type { ColumnConfig, TableHeaderProps, TableRowData } from '../../types';
import { buildColumnSizeStyle, readColumnSize } from '../../utils/columnSizing';
import { HeaderCellContent } from './HeaderCellContent';

export function SingleRowHeader<TRow extends TableRowData>({
  table,
  columns,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  enableSorting,
  enableRowExpansion,
  tableLayout = 'fixed',
}: TableHeaderProps<TRow>) {
  const headers = table.getFlatHeaders();

  return (
    <ShadcnTableHeader className="sticky top-0 z-20 border-b bg-muted">
      <TableRow className="border-b overflow-visible !bg-transparent hover:!bg-transparent">
        {enableRowExpansion && (
          <TableHead className="w-10 px-2" aria-hidden="true" />
        )}
        {headers.map(header => {
          const columnConfig = columns.find(
            col => col.id === header.id
          ) as ColumnConfig<TRow>;

          if (!columnConfig) {
            return (
              <TableHead
                key={header.id}
                style={buildColumnSizeStyle(
                  tableLayout,
                  readColumnSize(header.column)
                )}
                className="overflow-visible"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableHead>
            );
          }

          const headerText =
            typeof header.column.columnDef.header === 'string'
              ? header.column.columnDef.header
              : header.id;

          return (
            <TableHead
              key={header.id}
              style={buildColumnSizeStyle(
                tableLayout,
                readColumnSize(header.column)
              )}
              className={cn('overflow-visible', columnConfig.headerClassName)}
            >
              <HeaderCellContent
                column={columnConfig}
                headerText={headerText}
                filters={filters}
                onFilterChange={onFilterChange}
                sort={sort}
                onSortChange={onSortChange}
                enableSorting={enableSorting ?? false}
              />
            </TableHead>
          );
        })}
      </TableRow>
    </ShadcnTableHeader>
  );
}
